import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import API_URL from '../config/api';

// Utility function to format numbers with thousand separators and 2 decimals
const formatNumber = (num) => {
  if (!num || isNaN(num)) return '0.00';
  return parseFloat(num).toLocaleString('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Format percentage with decimals - convert decimal to percentage for display
const formatPercentage = (num) => {
  if (!num || isNaN(num)) return '0.00';
  return (parseFloat(num) * 100).toFixed(2); // Convert decimal to percentage
};

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TextField,
  IconButton,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Skeleton,
  LinearProgress,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  AutoFixHigh as AIIcon,
  Engineering as EngineeringIcon
} from '@mui/icons-material';

function AnalisisPUModal({ open, onClose, concept, onSave, porcentajes = {} }) {
  const [analisis, setAnalisis] = useState({
    materiales: [],
    manoObra: [],
    herramienta: [],
    maquinaria: []
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [savedAnalyses, setSavedAnalyses] = useState({});
  const [loading, setLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (concept && open) {
      // Primero verificar si el concepto ya tiene análisis
      if (concept.analisis) {
        // El concepto ya tiene análisis de la IA
        setAnalisis({
          materiales: concept.analisis.materiales || [],
          manoObra: concept.analisis.mano_obra || [],
          herramienta: concept.analisis.herramienta || [],
          maquinaria: concept.analisis.maquinaria || []
        });
        setHasChanges(false);
      } else {
        // Check if there's a saved analysis for this concept
        const savedKey = `${concept.codigo}_${concept.descripcion}`;
        if (savedAnalyses[savedKey]) {
          setAnalisis(savedAnalyses[savedKey]);
          setHasChanges(false);
        } else {
          // NO generar automáticamente, el usuario debe hacer clic en el botón
          // Inicializar con arrays vacíos
          setAnalisis({
            materiales: [],
            manoObra: [],
            herramienta: [],
            maquinaria: []
          });
          setHasChanges(false);
        }
      }
    }
  }, [concept, open]);

  const generarConIA = async () => {
    if (!concept) return;

    setAiGenerating(true);
    setError(null);
    setSuccessMessage('');

    try {
      console.log('Generando análisis con IA para:', concept.descripcion);

      const response = await axios.post(`${API_URL}/analisis-pu/generate`, {
        concepto: concept.descripcion,
        unidad: concept.unidad || 'PZA',
        ubicacion: 'Campeche'
      });

      if (response.data) {
        console.log('Análisis generado con IA:', response.data);

        // Convertir la respuesta al formato esperado
        const nuevoAnalisis = {
          materiales: response.data.materiales || [],
          manoObra: response.data.mano_obra || [],
          herramienta: response.data.herramienta || [],
          maquinaria: response.data.maquinaria || []
        };

        // Agregar herramienta menor si no existe
        if (nuevoAnalisis.herramienta.length === 0 && nuevoAnalisis.manoObra.length > 0) {
          const totalMO = nuevoAnalisis.manoObra.reduce((sum, item) => sum + (item.importe || 0), 0);
          nuevoAnalisis.herramienta = [{
            descripcion: 'Herramienta menor',
            unidad: '%MO',
            cantidad: 0.03,
            precio: totalMO,
            importe: totalMO * 0.03
          }];
        }

        setAnalisis(nuevoAnalisis);
        setSuccessMessage('✨ Análisis generado con IA exitosamente');
        setHasChanges(false);

        // Limpiar mensaje después de 3 segundos
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error generando análisis con IA:', error);
      setError('Error al generar análisis con IA. Usando análisis básico.');

      // Fallback a análisis automático básico
      const nuevoAnalisis = generarAnalisisAutomatico(concept);
      setAnalisis(nuevoAnalisis);

      // Limpiar error después de 5 segundos
      setTimeout(() => setError(null), 5000);
    } finally {
      setAiGenerating(false);
    }
  };

  const generarAnalisisAutomatico = (concepto) => {
    const tipoTrabajo = concepto.tipo_trabajo || 'general';
    let materiales = [];
    let manoObra = [];
    let herramienta = [];
    let maquinaria = [];

    switch (tipoTrabajo) {
      case 'excavacion':
        maquinaria = [
          { descripcion: 'Retroexcavadora CAT 320', unidad: 'HR', cantidad: roundToTwo(0.05), precio: roundToTwo(850), importe: roundToTwo(42.50) }
        ];
        manoObra = [
          { descripcion: 'Operador de maquinaria', unidad: 'JOR', cantidad: 0.05, precio: 607.50, importe: 30.38 },
          { descripcion: 'Ayudante general', unidad: 'JOR', cantidad: 0.05, precio: 378.00, importe: 18.90 }
        ];
        herramienta = [
          { descripcion: 'Herramienta menor', unidad: '%MO', cantidad: 0.03, precio: 0, importe: 0 }
        ];
        break;

      case 'cimentacion':
        materiales = [
          { descripcion: 'Concreto f\'c=250 kg/cm2', unidad: 'M3', cantidad: 1.05, precio: 2050, importe: 2152.50 },
          { descripcion: 'Acero de refuerzo fy=4200 kg/cm2', unidad: 'KG', cantidad: 80, precio: 25, importe: 2000 },
          { descripcion: 'Alambre recocido', unidad: 'KG', cantidad: 1.5, precio: 35, importe: 52.50 },
          { descripcion: 'Clavos', unidad: 'KG', cantidad: 0.5, precio: 28, importe: 14.00 }
        ];
        manoObra = [
          { descripcion: 'Oficial albañil', unidad: 'JOR', cantidad: 0.5, precio: 472.50, importe: 236.25 },
          { descripcion: 'Fierrero', unidad: 'JOR', cantidad: 0.3, precio: 540.00, importe: 162.00 },
          { descripcion: 'Ayudante', unidad: 'JOR', cantidad: 1, precio: 378.00, importe: 378.00 }
        ];
        herramienta = [
          { descripcion: 'Herramienta menor', unidad: '%MO', cantidad: 0.03, precio: 0, importe: 0 },
          { descripcion: 'Andamios y equipo', unidad: '%MO', cantidad: 0.05, precio: 0, importe: 0 }
        ];
        break;

      case 'albañileria':
        materiales = [
          { descripcion: 'Block hueco 15x20x40', unidad: 'PZA', cantidad: 12.5, precio: 19.50, importe: 243.75 },
          { descripcion: 'Mortero cemento-arena 1:4', unidad: 'M3', cantidad: 0.02, precio: 1850, importe: 37.00 },
          { descripcion: 'Cemento gris', unidad: 'KG', cantidad: 5, precio: 3.90, importe: 19.50 }
        ];
        manoObra = [
          { descripcion: 'Oficial albañil', unidad: 'JOR', cantidad: 0.08, precio: 472.50, importe: 37.80 },
          { descripcion: 'Ayudante', unidad: 'JOR', cantidad: 0.08, precio: 378.00, importe: 30.24 }
        ];
        herramienta = [
          { descripcion: 'Herramienta menor', unidad: '%MO', cantidad: 0.03, precio: 0, importe: 0 }
        ];
        break;

      default:
        materiales = [
          { descripcion: 'Material genérico', unidad: 'LOTE', cantidad: 1, precio: concepto.precio_unitario * 0.4, importe: concepto.precio_unitario * 0.4 }
        ];
        manoObra = [
          { descripcion: 'Oficial', unidad: 'JOR', cantidad: 0.1, precio: 472.50, importe: 47.25 },
          { descripcion: 'Ayudante', unidad: 'JOR', cantidad: 0.1, precio: 378.00, importe: 37.80 }
        ];
        herramienta = [
          { descripcion: 'Herramienta menor', unidad: '%MO', cantidad: 0.03, precio: 0, importe: 0 }
        ];
    }

    // Calcular importes de %MO
    const totalMO = manoObra.reduce((sum, item) => sum + item.importe, 0);
    herramienta = herramienta.map(item => {
      if (item.unidad === '%MO') {
        item.precio = totalMO;
        item.importe = item.cantidad * totalMO; // Direct multiplication since cantidad is already decimal
      }
      return item;
    });

    return { materiales, manoObra, herramienta, maquinaria };
  };

  const calcularTotales = () => {
    const totalMateriales = analisis.materiales.reduce((sum, item) => sum + (item.importe || 0), 0);
    const totalManoObra = analisis.manoObra.reduce((sum, item) => sum + (item.importe || 0), 0);
    const totalHerramienta = analisis.herramienta.reduce((sum, item) => sum + (item.importe || 0), 0);
    const totalMaquinaria = analisis.maquinaria.reduce((sum, item) => sum + (item.importe || 0), 0);

    const costoDirecto = totalMateriales + totalManoObra + totalHerramienta + totalMaquinaria;
    // Usar porcentajes de la configuración del presupuesto
    const indirectos = costoDirecto * ((porcentajes.indirectos || 17) / 100);
    const financiamiento = (costoDirecto + indirectos) * ((porcentajes.financiamiento || 2) / 100);
    const utilidad = (costoDirecto + indirectos + financiamiento) * ((porcentajes.utilidad || 10) / 100);

    return {
      totalMateriales,
      totalManoObra,
      totalHerramienta,
      totalMaquinaria,
      costoDirecto,
      indirectos,
      financiamiento,
      utilidad,
      precioUnitario: costoDirecto + indirectos + financiamiento + utilidad
    };
  };

  const handleAddItem = (categoria) => {
    const newItem = {
      descripcion: categoria === 'herramienta' ? 'Herramienta/Equipo' : 'Nuevo elemento',
      unidad: categoria === 'herramienta' ? '%MO' : 'PZA',
      cantidad: categoria === 'herramienta' ? 0.03 : 1,
      precio: 0,
      importe: 0
    };

    if (categoria === 'herramienta' && newItem.unidad === '%MO') {
      const totalMO = analisis.manoObra.reduce((sum, item) => sum + item.importe, 0);
      newItem.precio = totalMO;
      newItem.importe = newItem.cantidad * totalMO; // Direct multiplication
    }

    setAnalisis({
      ...analisis,
      [categoria]: [...analisis[categoria], newItem]
    });
    setHasChanges(true);
  };

  const handleUpdateItem = (categoria, index, field, value) => {
    const newItems = [...analisis[categoria]];
    newItems[index][field] = value;

    // Recalcular importe
    if (field === 'cantidad' || field === 'precio') {
      if (newItems[index].unidad === '%MO') {
        const totalMO = analisis.manoObra.reduce((sum, item) => sum + item.importe, 0);
        newItems[index].precio = totalMO;
        newItems[index].importe = (newItems[index].cantidad || 0) * totalMO; // Direct multiplication
      } else {
        newItems[index].importe = (newItems[index].cantidad || 0) * (newItems[index].precio || 0);
      }
    }

    // Si cambia la unidad a %MO
    if (field === 'unidad' && value === '%MO') {
      const totalMO = analisis.manoObra.reduce((sum, item) => sum + item.importe, 0);
      newItems[index].precio = totalMO;
      newItems[index].importe = (newItems[index].cantidad || 0) * totalMO; // Direct multiplication
    } else if (field === 'unidad' && value !== '%MO' && newItems[index].unidad === '%MO') {
      newItems[index].precio = 0;
      newItems[index].importe = 0;
    }

    setAnalisis({
      ...analisis,
      [categoria]: newItems
    });
    setHasChanges(true);

    // Actualizar herramientas con %MO si se modificó mano de obra
    if (categoria === 'manoObra') {
      const totalMO = newItems.reduce((sum, item) => sum + item.importe, 0);
      const updatedHerramienta = analisis.herramienta.map(item => {
        if (item.unidad === '%MO') {
          item.precio = totalMO;
          item.importe = (item.cantidad || 0) * totalMO; // Direct multiplication
        }
        return item;
      });
      setAnalisis(prev => ({
        ...prev,
        herramienta: updatedHerramienta
      }));
    }
  };

  const handleDeleteItem = (categoria, index) => {
    const newAnalisis = {
      ...analisis,
      [categoria]: analisis[categoria].filter((_, i) => i !== index)
    };
    setAnalisis(newAnalisis);
    setHasChanges(true);

    // Actualizar %MO si se borró mano de obra
    if (categoria === 'manoObra') {
      const totalMO = newAnalisis.manoObra.reduce((sum, item) => sum + item.importe, 0);
      const updatedHerramienta = newAnalisis.herramienta.map(item => {
        if (item.unidad === '%MO') {
          item.precio = totalMO;
          item.importe = (item.cantidad || 0) * totalMO; // Direct multiplication
        }
        return item;
      });
      newAnalisis.herramienta = updatedHerramienta;
      setAnalisis(newAnalisis);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('¿Desea aplicar los cambios al presupuesto?')) {
        handleSave();
      } else {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleSave = () => {
    const totales = calcularTotales();

    // Save the current analysis for this concept
    const savedKey = `${concept.codigo}_${concept.descripcion}`;
    setSavedAnalyses(prev => ({
      ...prev,
      [savedKey]: analisis
    }));

    if (onSave) {
      // Guardar el costo directo y el análisis completo
      // Convertir manoObra a mano_obra para consistencia con el backend
      const analisisParaGuardar = {
        materiales: analisis.materiales,
        mano_obra: analisis.manoObra, // Cambiar de manoObra a mano_obra
        herramienta: analisis.herramienta,
        maquinaria: analisis.maquinaria
      };
      onSave(totales.costoDirecto, analisisParaGuardar);
    }
    onClose();
  };

  const totales = calcularTotales();

  if (!concept) return null;

  const renderTable = (items, categoria, title) => (
    <Accordion defaultExpanded sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
          <Typography sx={{ flexGrow: 1 }}>{title}</Typography>
          <Chip
            label={`$${formatNumber(totales[`total${categoria.charAt(0).toUpperCase() + categoria.slice(1)}`])}`}
            color="primary"
            size="small"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              startIcon={<AddIcon />}
              size="small"
              variant="outlined"
              onClick={() => handleAddItem(categoria)}
            >
              Agregar
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="40%">Descripción</TableCell>
                  <TableCell width="15%">Unidad</TableCell>
                  <TableCell width="15%" align="right">Cantidad</TableCell>
                  <TableCell width="15%" align="right">P.U.</TableCell>
                  <TableCell width="15%" align="right">Importe</TableCell>
                  <TableCell width="50px"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        value={item.descripcion}
                        onChange={(e) => handleUpdateItem(categoria, index, 'descripcion', e.target.value)}
                        variant="standard"
                        fullWidth
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.unidad}
                        onChange={(e) => handleUpdateItem(categoria, index, 'unidad', e.target.value)}
                        variant="standard"
                        size="small"
                        fullWidth
                        placeholder="%MO"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={item.unidad === '%MO' ? formatPercentage(item.cantidad) : item.cantidad}
                        onChange={(e) => {
                          const inputValue = parseFloat(e.target.value) || 0;
                          const finalValue = item.unidad === '%MO' ? inputValue / 100 : inputValue; // Convert percentage to decimal
                          handleUpdateItem(categoria, index, 'cantidad', finalValue);
                        }}
                        variant="standard"
                        size="small"
                        fullWidth
                        inputProps={{
                          style: { textAlign: 'right' },
                          step: item.unidad === '%MO' ? '0.01' : '1'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {item.unidad === '%MO' ? (
                        <Typography variant="body2" color="textSecondary">
                          ${formatNumber(item.precio)}
                        </Typography>
                      ) : (
                        <TextField
                          type="number"
                          value={item.precio}
                          onChange={(e) => handleUpdateItem(categoria, index, 'precio', parseFloat(e.target.value) || 0)}
                          variant="standard"
                          size="small"
                          fullWidth
                          inputProps={{ style: { textAlign: 'right' } }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        ${formatNumber(item.importe)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteItem(categoria, index)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Análisis de Precio Unitario</Typography>
            <Typography variant="subtitle2" color="textSecondary">
              {concept.codigo} - {concept.descripcion}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Unidad: {concept.unidad} | Tipo: {concept.tipo_trabajo}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={aiGenerating ? null : <AIIcon />}
              onClick={generarConIA}
              disabled={aiGenerating}
            >
              {aiGenerating ? 'Generando con IA...' : (analisis.materiales.length > 0 ? 'Regenerar con IA' : 'Generar con IA')}
            </Button>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        {aiGenerating && <LinearProgress sx={{ mt: 1 }} />}
      </DialogTitle>

      <DialogContent dividers>
        {/* Mensajes de estado */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {aiGenerating && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<EngineeringIcon />}>
            <Typography variant="subtitle2" gutterBottom>
              🤖 Analizando concepto con Inteligencia Artificial...
            </Typography>
            <Typography variant="body2">
              • Identificando materiales necesarios según catálogos de Campeche
              <br />
              • Calculando rendimientos de mano de obra
              <br />
              • Determinando maquinaria requerida
              <br />
              • Aplicando precios actualizados de la base de datos
            </Typography>
          </Alert>
        )}

        {/* Tablas de recursos */}
        {!aiGenerating && (
          <>
            {renderTable(analisis.materiales, 'materiales', 'Materiales')}
            {renderTable(analisis.manoObra, 'manoObra', 'Mano de Obra')}
            {renderTable(analisis.herramienta, 'herramienta', 'Herramienta y Equipo Menor')}
            {renderTable(analisis.maquinaria, 'maquinaria', 'Maquinaria')}
          </>
        )}

        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" gutterBottom>Resumen del Análisis</Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={8}>
              <Typography variant="body2" color="textSecondary">Materiales:</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="body1">${formatNumber(totales.totalMateriales)}</Typography>
            </Grid>

            <Grid item xs={8}>
              <Typography variant="body2" color="textSecondary">Mano de Obra:</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="body1">${formatNumber(totales.totalManoObra)}</Typography>
            </Grid>

            <Grid item xs={8}>
              <Typography variant="body2" color="textSecondary">Herramienta y Equipo Menor:</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="body1">${formatNumber(totales.totalHerramienta)}</Typography>
            </Grid>

            <Grid item xs={8}>
              <Typography variant="body2" color="textSecondary">Maquinaria:</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="body1">${formatNumber(totales.totalMaquinaria)}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={8}>
              <Typography variant="subtitle1" fontWeight="bold">Costo Directo:</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="subtitle1" fontWeight="bold">
                ${formatNumber(totales.costoDirecto)}
              </Typography>
            </Grid>

            <Grid item xs={8}>
              <Typography variant="body2" color="textSecondary">Indirectos (17%):</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="body1">${formatNumber(totales.indirectos)}</Typography>
            </Grid>

            <Grid item xs={8}>
              <Typography variant="body2" color="textSecondary">Financiamiento (2%):</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="body1">${formatNumber(totales.financiamiento)}</Typography>
            </Grid>

            <Grid item xs={8}>
              <Typography variant="body2" color="textSecondary">Utilidad (10%):</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="body1">${formatNumber(totales.utilidad)}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={8}>
              <Typography variant="h6" color="primary">PRECIO UNITARIO:</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="h6" color="primary">
                ${formatNumber(totales.precioUnitario)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={<SaveIcon />}
        >
          Aplicar P.U. al Presupuesto
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AnalisisPUModal;